import { type useMutation } from '@apollo/client/react';
import loadable from '@loadable/component';
import { type FunctionComponent, useContext, useState } from 'react';
import { type AddProductToCartMutation } from '../../../../../__generated__/graphql-client-types';
import { ProductFinish } from '../../../../../components/product-components/product-finish/product-finish.component';
import { ProductMedia } from '../../../../../components/product-components/product-media/product-media.component';
import { ProductPrice } from '../../../../../components/product-components/product-price/product-price.component';
import { ProductSaleBanner } from '../../../../../components/product-components/product-sale-banner/product-sale-banner.component';
import { ProductSummary } from '../../../../../components/product-components/product-summary/product-summary.component';
import { SaleRestrictions } from '../../../../../components/product-components/sale-restrictions/sale-restrictions.component';
import { ProductRebate } from '../../../../../components/rebates/product-rebate/product-rebate.component';
import { ProductRebateWidget } from '../../../../../components/rebates/widgets/product-rebate-widget/product-rebate-widget.component';
import { FEATURE_FLAGS } from '../../../../../constants/feature-flags';
import { VariantContext } from '../../../../../contexts/variant/variant.context';
import { type ADD_TO_CART_BUTTON_TYPE } from '../../../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { useSiteViewPreference } from '../../../../../hooks/apollo/employee/employee.hooks';
import { useConfiguration } from '../../../../../hooks/configuration/configuration.hooks';
import { useFeature } from '../../../../../hooks/features/features.hooks';
import { type PdpProductFamily } from '../../../../../types/product.types';
import { ProductControls } from '../product-controls/product-controls.component';

const LoadableInternalBetterValueAlternative = loadable(
	() =>
		import(
			/* webpackChunkName: "internalproductinformation" */ '../../../../../components/product-components/internal-better-value-alternative/internal-better-value-alternative.component'
		),
	{
		resolveComponent: ({ InternalBetterValueAlternative }) => InternalBetterValueAlternative
	}
);

export type PdpBuySectionProps = {
	product: PdpProductFamily;
	productUid: number;
	nonStockMode: boolean;
	familyId: number;
	onAddToCartClick: (addToCartButtonType?: ADD_TO_CART_BUTTON_TYPE) => void;
	isAddingToCart: boolean;
	resetAddToCart: () => void;
	addToCartStatus: useMutation.Result<AddProductToCartMutation>;
	isStickyMediaGallery?: boolean;
};

export const PdpBuySection: FunctionComponent<PdpBuySectionProps> = ({
	product,
	productUid,
	nonStockMode,
	familyId,
	isAddingToCart,
	onAddToCartClick,
	resetAddToCart,
	addToCartStatus
}) => {
	const { currentVariant, selectedVariant } = useContext(VariantContext);
	const [units, setUnits] = useState(0);
	const { status } = useConfiguration({ familyId: Number(familyId) });
	const { showAsEmployee } = useSiteViewPreference();
	const ecoRebatesWidgetsActive = useFeature(FEATURE_FLAGS.ECOREBATES_WIDGETS);

	return (
		<div className={'cf flex db-ns flex-column'} data-testid="pdp-buy-section">
			<div className={`fl w-50-ns order-1 pt1-ns pr3-ns`} data-testid="pdp-buy-section-container">
				{/* Product Image */}
				<ProductMedia product={product} />
				{showAsEmployee && (
					<div className="mt4 ml2 dn db-ns">
						<LoadableInternalBetterValueAlternative familyId={Number(familyId)} />
					</div>
				)}
			</div>
			<div className={'fl w-50-ns order-0'}>
				<ProductSummary
					brand={product.brandInfo.name}
					modelNumber={currentVariant.modelNumber}
					productUid={productUid}
					title={product.title}
					collectionName={product.collection ? product.collection.name : ''}
					shouldClampTitle={false}
					reviewSummary={product.reviewSummary}
				/>
			</div>
			<div className={'fr w-100 w-50-ns order-2'}>
				<ProductSaleBanner productSales={selectedVariant.sales} />
				{selectedVariant &&
					(ecoRebatesWidgetsActive ? (
						<ProductRebateWidget type="PDP" variantIds={[selectedVariant.id]} className="mt2" />
					) : (
						<ProductRebate variantIds={[selectedVariant.id]} />
					))}
				{selectedVariant?.saleRestrictions && (
					<SaleRestrictions
						{...selectedVariant.saleRestrictions}
						currentFamilyId={product.id}
						className={`mv3`}
						selectedVariant={selectedVariant}
					/>
				)}
				<ProductPrice
					{...currentVariant.priceInfo}
					pricingDisplay={currentVariant.productPricingDisplay}
					variantId={currentVariant.id}
					className={`mr4 f4 f3-ns pt2`}
				/>
				<ProductFinish isPDP nonStockMode={nonStockMode} productUid={productUid} />
				<ProductControls
					onAddToCartClick={onAddToCartClick}
					resetAddToCart={resetAddToCart}
					addToCartStatus={addToCartStatus}
					units={{ unitsValue: units, setUnits }}
					status={status}
					isAddingToCart={isAddingToCart}
				/>
			</div>
		</div>
	);
};
